import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import CheckoutFaqs from "./CheckoutFaqs";

const ICONS = "/assets/mobile/checkout";
const APPLY_GRADIENT = "linear-gradient(1.08deg, #a28fdc 0.74%, #7c3aed 74.61%)";
const PROCEED_GRADIENT = "linear-gradient(10.42deg, #9e85dd 25%, #7b39ec 59.86%)";

const RENT_GUIDELINES = [
  ["Fuel, Tolls, Parking, and Inter-State Taxes:", " These charges are to be borne by the customer."],
  ["Change in Pricing Plan (Without Fuel):", " The pricing plan cannot be changed once the booking is confirmed."],
  ["Required Documents for KYC Verification:", " Driving License, Aadhar Card, and one of the following: Employee ID card / Student ID card / GST Certificate (for business holders)."],
  ["For local residents of the vehicle pickup city:", " A valid address proof such as a Rental Agreement, Gas Bill, Utility Bill, Communication Bill, or PG Accommodation Bill.", "For non-local residents: Travel tickets and hotel reservation."],
  ["Age Criteria:", " The minimum permissible age for renting a Wowcarz car is 21 years."],
  ["ID Verification:", " The driving license must be at least one year old as of the rental start date. At the time of car pickup, the booking person must have the original driving license or its DigiLocker version handy. Our executive will physically verify the license and require one original document to be submitted. This verification process is mandatory. If you are unable to provide these documents, we will not be able to hand over the car, and the booking will be treated as a late cancellation, incurring a charge of 100% of the fare."],
];

const formatDateParts = (dateStr) => {
  if (!dateStr) return { date: "—", time: "" };
  const d = new Date(dateStr);
  if (isNaN(d)) return { date: dateStr, time: "" };
  return {
    date: d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
  };
};

const PlanCol = ({ label, value, valueColor = "text-[#1f2937]" }) => (
  <div className="flex flex-1 flex-col gap-1 items-start min-w-0">
    <span className="block text-[#959595] text-[clamp(10.5px,2.9vw,12px)] font-medium leading-4 text-left">{label}</span>
    <span className={`block ${valueColor} text-[clamp(11px,3.4vw,14px)] font-semibold tracking-[0.42px] max-[350px]:tracking-normal uppercase leading-5 text-left break-words max-w-full`}>{value}</span>
  </div>
);

const DateItem = ({ label, dateStr }) => {
  const { date, time } = formatDateParts(dateStr);
  return (
    <div className="flex flex-1 flex-col gap-1.5 items-start min-w-0 bg-[#f9fafb] border border-[#e5e7eb] rounded-[10px] px-[clamp(8px,2.9vw,12px)] py-2">
      <span className="block text-[#959595] text-[clamp(9px,2.6vw,12px)] font-medium uppercase leading-4 text-left">{label}</span>
      <div className="flex flex-col gap-3 items-start">
        <span className="block text-[#1f2937] text-[clamp(12px,3.4vw,14px)] font-bold leading-5 text-left">{date}</span>
        {time && <span className="block text-[#959595] text-[clamp(11px,2.9vw,12px)] font-medium leading-4 text-left">{time}</span>}
      </div>
    </div>
  );
};

// Mobile (<768px) checkout. All state, API calls and handlers stay in Checkout.
const CheckoutMob = ({
  carData,
  savedImageUrl,
  bookingTypeTag,
  isEvBooking,
  evDrivingRange,
  evChargingType,
  features,
  cityname,
  isSubscription,
  packageKmLabel,
  allottedKmLabel,
  extraChargeLabel,
  payableAmount,
  formattedStartDate,
  formattedEndDate,
  tripDuration,
  deliveryOptions,
  deliverytype,
  isDeliveryOptionDisabled,
  handleDeliveryTypeChange,
  shortSubHomeDeliveryBlocked,
  minHoursHomeDeliveryBlocked,
  homeDeliveryBookingHr,
  pickupInputProps,
  pickupSuggestionList,
  dropInputProps,
  dropSuggestionList,
  dropOption,
  handleDropOptionChange,
  airportDelivery,
  handleAirportChange,
  couponpostresponse,
  coupondata,
  setcouponopen,
  handleRemoveCoupon,
  manualCouponCode,
  setManualCouponCode,
  manualCouponLoading,
  handleManualCouponApply,
  agreement,
  setAgreement,
  setTandcOpen,
  showModal,
  handleProceed,
}) => {
  const payableLabel = Number(payableAmount).toLocaleString("en-IN");

  const featureLabels = (isEvBooking
    ? [
        features.seats ? `${features.seats} Seater` : null,
        features.transmission,
        features.bodyType,
        evChargingType ? `${evChargingType} Charging` : null,
        evDrivingRange ? `Driving Range : ${evDrivingRange} Km` : null,
      ]
    : [
        features.seats ? `${features.seats} Seats` : null,
        features.transmission,
        features.fuel,
        features.bodyType,
      ]
  ).filter(Boolean);

  const inputClass = "flex-1 min-w-0 bg-transparent text-[#1f2937] text-[14px] font-medium leading-normal outline-none overflow-hidden text-ellipsis placeholder:text-[#9ca3af] placeholder:font-normal disabled:opacity-100 disabled:[-webkit-text-fill-color:#1f2937]";

  return (
    <div className="flex flex-col gap-[22px] w-full min-w-0 font-['Outfit',sans-serif] text-left pb-[88px]">

      {/* ── Left column content ── */}
      <div className="flex flex-col gap-8 w-full">

        {/* Hero Car Card */}
        <div className="bg-white border border-[#f4efee] rounded-2xl flex flex-col gap-5 px-4 py-[18px]">
          <div className="flex items-center gap-2 w-full">
            <div className="w-[44%] max-w-[260px] aspect-[167/122] shrink-0 flex items-center justify-center">
              <img
                src={carData?.vehicleDetails?.imageUrl || carData?.vehicleDetails?.vehicleImgThumb || carData?.imageUrl || savedImageUrl || "/assets/logo.png"}
                alt={carData?.vehicleName || "Car"}
                className="w-full h-full object-contain scale-[1.15] origin-center"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col gap-3 flex-1 min-w-0 items-start">
              <div className="flex flex-col gap-2 items-start w-full">
                <div className="flex items-center justify-between flex-wrap gap-2 w-full">
                  <p className="text-[#1f2937] text-[14px] font-semibold tracking-[0.14px] text-left min-w-0">
                    {carData?.vehicleName || "Your Vehicle"}
                  </p>
                  {bookingTypeTag}
                </div>
              </div>
              <div className={isEvBooking ? "flex flex-wrap gap-2 w-full" : "grid grid-cols-1 min-[350px]:grid-cols-[auto_1fr] gap-2 w-full"}>
                {featureLabels.map((feature) => (
                  <div
                    key={feature}
                    className={`flex flex-auto items-center gap-[clamp(3px,1.4vw,6px)] px-[clamp(5px,1.9vw,8px)] py-1.5 rounded-lg max-w-full ${isEvBooking ? "bg-[#f3f4f6]" : "bg-[#ecfdf5]"}`}
                  >
                    {!isEvBooking && <img src={`${ICONS}/check-circle.svg`} alt="" className="shrink-0" />}
                    <span className="text-[#5c5c5c] text-[clamp(10.5px,2.9vw,12px)] font-medium text-left min-w-0">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <hr className="border-[#e5e7eb] w-full" />
          <p className="text-[#9ca3af] text-[11px] font-normal leading-4 text-left">
            Disclaimer: The car image shown is for representation purposes only. Actual color, appearance, and specific features of the vehicle may vary depending on local fleet availability at the pickup time.
          </p>
        </div>

        {/* Pricing & Plan + Booking Summary */}
        <div className="flex flex-col gap-3 w-full">
          <div className="flex gap-2 items-start w-full">
            <div className="flex flex-1 flex-col gap-4 min-w-0 self-stretch">
              <div className="flex gap-[clamp(8px,3.9vw,16px)] items-start">
                <PlanCol label="City" value={cityname || "—"} />
                {!isSubscription && <PlanCol label="Selected package" value={packageKmLabel} />}
              </div>
              <div className="flex gap-[clamp(8px,3.9vw,16px)] items-start">
                <PlanCol label="Allotted KM" value={allottedKmLabel} valueColor="text-[#10b981]" />
                <PlanCol label="Extra Charges" value={extraChargeLabel} />
              </div>
            </div>
            <div className="bg-[#f6f1ff] rounded-[10px] min-h-[94px] p-[clamp(10px,3.9vw,16px)] flex flex-col gap-3 items-start justify-center shrink-0 font-bold">
              <span className="block text-[#808080] text-[clamp(11px,2.9vw,12px)] leading-4">Amount to Pay</span>
              <span className="block text-[#cd2525] text-[clamp(17px,5.3vw,22px)] leading-7 whitespace-nowrap">₹{payableLabel} /-</span>
            </div>
          </div>

          <hr className="border-[#e5e7eb] w-full" />

          <div className="bg-white border border-[#f4efee] rounded-2xl p-3 flex flex-col gap-3 w-full">
            <span className="block text-[#1a1917] text-[15px] font-semibold leading-6 text-left">Booking Summary &amp; Schedule</span>
            <div className="flex items-center gap-[clamp(6px,4.3vw,18px)] w-full">
              <DateItem label="Start Date & Time" dateStr={formattedStartDate} />
              <div className="bg-[#f3f4f6] rounded-[20px] size-6 flex items-center justify-center shrink-0">
                <img src={`${ICONS}/arrow-right.svg`} alt="" className="shrink-0" />
              </div>
              <DateItem label="End Date & Time" dateStr={formattedEndDate} />
            </div>
            {tripDuration && (
              <div className="bg-[rgba(182,139,255,0.12)] rounded-[10px] px-3 py-2 flex items-center gap-3 w-full">
                <img src={`${ICONS}/clock.svg`} alt="" className="shrink-0" />
                <span className="block text-[#7c3aed] text-[12px] font-semibold leading-5 flex-1 min-w-0 text-left">
                  Total Trip Duration: <span className="font-bold">{tripDuration}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Right column content ── */}
      <div className="flex flex-col gap-4 w-full">

        {/* Pickup & Delivery */}
        <div className="bg-white border border-[#f4efee] rounded-2xl p-[clamp(14px,5.8vw,24px)] flex flex-col gap-4 w-full">
          <p className="text-[#1a1917] text-[16px] font-semibold leading-6 text-left">Pickup &amp; Delivery</p>

          <div className="flex gap-[clamp(8px,2.9vw,12px)] h-[41px] w-full">
            {deliveryOptions.map((option) => {
              const isDisabled = isDeliveryOptionDisabled(option);
              const isActive = deliverytype === option.label;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleDeliveryTypeChange(option.label)}
                  className={`flex flex-1 min-w-0 h-full items-center justify-center gap-2 px-2 rounded-[10px] text-[clamp(12px,3.4vw,14px)] whitespace-nowrap
                    ${isActive ? "bg-[rgba(124,58,237,0.06)] border-2 border-[#a28fdc] text-[#0c2d75] font-bold" :
                      isDisabled ? "bg-white border border-[#e5e7eb] text-gray-400 font-medium cursor-not-allowed opacity-60" :
                      "bg-white border border-[#e5e7eb] text-[#6b7280] font-medium"}`}
                >
                  <img src={`${ICONS}/${isActive ? "ellipse-active" : "ellipse-inactive"}.svg`} alt="" className="shrink-0" />
                  {option.label}
                </button>
              );
            })}
          </div>

          {shortSubHomeDeliveryBlocked && (
            <small className="text-xs text-red-500 text-left">
              Note: Home delivery is not available for short subscriptions (7 days)
            </small>
          )}
          {!shortSubHomeDeliveryBlocked && minHoursHomeDeliveryBlocked && (
            <small className="text-xs text-red-500 text-left">
              Note: Home delivery requires a minimum booking duration of {homeDeliveryBookingHr} hours
            </small>
          )}

          {/* Pickup location */}
          <div className="flex flex-col gap-1.5 w-full">
            <p className="text-[#6b7280] text-[12px] font-semibold uppercase text-left">Pick Up Location</p>
            <div className="relative bg-[#fafafa] border border-[#e5e7eb] rounded-[10px] px-3.5 py-3 flex items-center gap-2.5 w-full">
              <img src={`${ICONS}/map-pin-drop.svg`} alt="" className="shrink-0" />
              <input {...pickupInputProps} className={inputClass} />
              <img src="/assets/checkout/icon.svg" alt="" className="shrink-0 rotate-90" />
              {pickupSuggestionList}
            </div>
          </div>

          {/* Airport delivery */}
          {deliverytype === "Home Delivery" && cityname !== "Vellore" && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="airportDeliveryMob" checked={airportDelivery} onChange={handleAirportChange} className="h-4 w-4" />
              <label htmlFor="airportDeliveryMob" className="text-sm font-medium text-gray-700">is Airport Delivery</label>
            </div>
          )}

          {/* Drop location */}
          <div className="flex flex-col gap-1.5 w-full">
            <p className="text-[#1f2937] text-[14px] font-semibold text-left">Drop location</p>
            <div className={`relative ${dropOption === "select" ? "w-[90px]" : "w-[170px]"}`}>
              <select
                value={dropOption}
                onChange={handleDropOptionChange}
                className="w-full appearance-none bg-white border border-[#e5e7eb] rounded-lg pl-3.5 pr-7 py-1.5 text-[14px] font-normal text-[#374151] outline-none"
              >
                <option value="select">select</option>
                <option value="sameaspickup">Same as Pickup</option>
                {!minHoursHomeDeliveryBlocked && (
                  <option value="differentlocation">Different Location</option>
                )}
              </select>
              <img
                src="/assets/checkout/chevron-down.svg"
                alt=""
                className="absolute right-2 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none"
              />
            </div>
            <div className="relative bg-[#fafafa] border border-[#e5e7eb] rounded-[10px] px-3.5 py-3 flex items-center gap-2.5 w-full">
              <img src={`${ICONS}/map-pin-drop.svg`} alt="" className="shrink-0" />
              <input {...dropInputProps} className={inputClass} />
              {dropSuggestionList}
            </div>
          </div>

          <p className="text-[#6b7280] text-[11px] text-left w-full">* Delivery charges applicable as extra according to hub to delivery address distance.</p>
          <small className="text-xs text-left text-gray-500 w-full">Disclaimer: Delivery Charges may vary for above 20+ km / outside city limits locations. The same will be confirmed upon KYC verification.</small>
        </div>

        {/* Coupons */}
        <AnimatePresence mode="wait">
          {couponpostresponse ? (
            <motion.div
              key="applied"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="bg-white border border-[#f4efee] rounded-2xl flex flex-wrap items-center gap-3 p-3 w-full"
            >
              <div className="flex items-center gap-3 flex-1 min-w-[180px]">
                <div className="bg-[#e4fff5] rounded-[10px] size-8 flex items-center justify-center shrink-0">
                  <img src={`${ICONS}/ticket.svg`} alt="" className="shrink-0" style={{ filter: "invert(46%) sepia(74%) saturate(462%) hue-rotate(112deg) brightness(96%) contrast(101%)" }} />
                </div>
                <div className="flex flex-col gap-0.5 items-start flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="text-[#00a86b] text-sm font-bold tracking-wide break-all">{couponpostresponse?.couponCode}</span>
                    <span className="bg-[#e4fff5] border border-[#a7f3d0] text-[#065f46] text-[10px] font-bold px-2 py-0.5 rounded-full">Applied ✓</span>
                  </div>
                  {couponpostresponse?.discountAmount > 0 && (
                    <p className="text-[#6b7280] text-[11px] text-left">
                      You save <span className="text-[#00a86b] font-semibold">₹{couponpostresponse.discountAmount}</span> with this coupon
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-auto">
                <button type="button" onClick={() => setcouponopen(true)} className="px-3 py-1.5 rounded-lg border border-[#e5e7eb] text-[#374151] text-[12px] font-semibold">
                  Edit
                </button>
                <button type="button" onClick={handleRemoveCoupon} className="px-3 py-1.5 rounded-lg border border-[#fee2e2] text-[#dc2626] text-[12px] font-semibold">
                  Remove
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="not-applied"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }}
              className="flex flex-col gap-4 w-full"
            >
              <button
                type="button"
                disabled={coupondata.length < 1}
                onClick={() => setcouponopen(true)}
                className="bg-white border border-[#f4efee] rounded-2xl flex items-center gap-3 p-3 w-full disabled:cursor-not-allowed disabled:opacity-60"
              >
                <div className="bg-[#f8f8fa] rounded-[10px] size-8 flex items-center justify-center shrink-0">
                  <img src={`${ICONS}/ticket.svg`} alt="" className="shrink-0" />
                </div>
                <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
                  <div className="flex flex-col gap-0.5 items-start">
                    <p className="text-[#1a1a1a] text-[14px] font-semibold text-left">Coupons</p>
                    <div className="flex items-center gap-2 py-1">
                      <p className="text-[#a3a3a3] text-[10px] font-medium leading-none text-left">View all coupons</p>
                      <img src={`${ICONS}/view-all-chevron.svg`} alt="" className="shrink-0 -rotate-90" />
                    </div>
                  </div>
                  {coupondata.length > 0 ? (
                    <div className="bg-[#e4fff5] h-6 px-3 flex items-center justify-center rounded-md shrink-0">
                      <p className="text-[#00a86b] text-[12px] font-semibold whitespace-nowrap">{coupondata.length} Available</p>
                    </div>
                  ) : (
                    <p className="text-[#6b7280] text-xs shrink-0">No coupons available</p>
                  )}
                </div>
              </button>

              <div className="flex flex-col gap-2 w-full">
                <p className="text-[#374151] text-[13px] font-semibold text-left">Have a promo code?</p>
                <div className="border border-[#f2ebff] rounded-[10px] h-12 flex items-center justify-between gap-2 pl-3.5 pr-1 w-full">
                  <input
                    type="text"
                    placeholder="Enter coupon manually..."
                    value={manualCouponCode}
                    onChange={(e) => setManualCouponCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleManualCouponApply()}
                    className="flex-1 min-w-0 bg-transparent text-[#1f2937] text-[14px] font-normal outline-none placeholder:text-[#9ca3af]"
                  />
                  <button
                    type="button"
                    onClick={handleManualCouponApply}
                    disabled={manualCouponLoading}
                    className="flex items-center justify-center px-5 py-2.5 rounded-lg text-white text-[13px] font-bold shrink-0"
                    style={{ background: APPLY_GRADIENT }}
                  >
                    {manualCouponLoading ? "..." : "Apply"}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Payment CTA */}
        <div className="bg-white rounded-2xl drop-shadow-[0px_4px_8px_rgba(0,0,0,0.03)] flex flex-col gap-4 p-4 w-full">
          <div className="flex items-start gap-2.5 w-full">
            <div
              role="checkbox"
              aria-checked={agreement}
              tabIndex={0}
              onClick={() => setAgreement(!agreement)}
              onKeyDown={(e) => e.key === " " && setAgreement(!agreement)}
              className={`rounded size-[18px] shrink-0 flex items-center justify-center cursor-pointer border-2 transition-colors ${agreement ? "bg-[#2563eb] border-[#2563eb]" : "bg-white border-[#d1d5db]"}`}
            >
              {agreement && <img src={`${ICONS}/check.svg`} alt="" className="shrink-0" />}
            </div>
            <div className="flex flex-col gap-1 items-start flex-1 min-w-0 text-[12px]">
              <p className="text-[#1f2937] font-semibold text-left">I hereby agree to terms and conditions with the company.</p>
              <span onClick={() => setTandcOpen(true)} className="text-[#2563eb] font-normal underline cursor-pointer text-left">View Agreement Policy Document</span>
            </div>
          </div>

          <hr className="border-[#e5e7eb] w-full" />

          <div className="flex flex-col gap-2 w-full">
            <div className="flex items-start justify-between gap-2 w-full">
              <p className="text-[#6b7280] text-[clamp(11px,2.9vw,12px)] font-semibold text-left">TOTAL RENTAL PAYABLE</p>
              {carData && (
                <button
                  type="button"
                  onClick={showModal}
                  className="bg-white border-[0.8px] border-[#072a79] px-2 py-1 rounded-md flex items-center justify-center shrink-0 w-[clamp(104px,31vw,128px)]"
                >
                  <span className="text-[#0a3085] text-[clamp(12px,3.4vw,14px)] font-medium whitespace-nowrap">Fare Summary</span>
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <p className="text-[#cd2525] text-[clamp(26px,7.8vw,32px)] font-bold">₹{payableLabel}</p>
              <p className="text-[#6b7280] text-[clamp(12px,3.4vw,14px)] font-normal">+ Inc tax &amp; security deposit</p>
            </div>
          </div>

          {carData && (
            <button
              type="button"
              onClick={handleProceed}
              className="w-full h-16 flex items-center justify-center rounded-xl text-white text-[16px] font-semibold drop-shadow-[0px_8px_10px_rgba(37,99,235,0.25)]"
              style={{ backgroundImage: PROCEED_GRADIENT }}
            >
              Proceed &amp; Pay
            </button>
          )}
        </div>

        {isEvBooking && <CheckoutFaqs />}

        {/* Terms & Conditions */}
        <div className="bg-white border border-[#f4efee] rounded-2xl p-4 flex flex-col gap-4 w-full">
          <div className="flex items-center justify-between gap-2 w-full text-[16px] font-semibold">
            <p className="text-[#1a1917] leading-6 text-left">Terms and Conditions</p>
            <span onClick={() => setTandcOpen(true)} className="text-[#5454d4] underline cursor-pointer shrink-0">View All</span>
          </div>
          <div className="flex flex-col gap-2 items-start w-full">
            <p className="text-[#1f2937] text-[12px] font-bold text-left">Cancellation and Changes Policy:</p>
            <div className="flex flex-col gap-[18px] text-[#6b7280] text-[12px] font-normal leading-[18px] w-full">
              <p className="text-left">Customers can cancel the booking subject to the following conditions and charges. A cancellation refund will be done through the original payment method within 10 bank working days.</p>
              <p className="text-left">Customer should inform 24 hours prior the booking start time, in such instances, 70% of the amount will be refunded. If failed to inform 24 hours prior to the booking start time, no refund will be issued.</p>
              <p className="text-left"><span className="font-bold text-[#1f2937]">Please Note</span>: Any booking made within 24 hours of the start time will not be eligible for cancellation. Any cancellation request in such cases will result in a 100% penalty.</p>
            </div>
          </div>
        </div>

        {/* Important Rent Information & Guidelines */}
        <div className="bg-white border border-[#e5e7eb] rounded-2xl drop-shadow-[0px_4px_8px_rgba(0,0,0,0.03)] flex flex-col gap-6 p-4 w-full">
          <div className="flex items-center gap-4 w-full">
            <div className="bg-[#ed1c24] h-16 w-2 rounded shrink-0" />
            <div className="flex flex-col gap-1 items-start flex-1 min-w-0 text-[14px]">
              <span className="block text-[#ed1c24] font-extrabold tracking-[1.2px] uppercase leading-5 text-left">IMPORTANT</span>
              <span className="block text-[#1a1917] font-semibold leading-6 text-left">Important Rent Information &amp; Guidelines</span>
            </div>
          </div>

          <hr className="border-[#e5e7eb] w-full" />

          <div className="flex flex-col gap-5 w-full">
            {RENT_GUIDELINES.map(([title, body, note]) => (
              <div key={title} className="text-[#1f2937] text-[13px] font-semibold leading-[22px]">
                <p className="text-left">
                  <span className="text-[#ed1c24] font-extrabold">{title}</span>
                  {body}
                </p>
                {note && <p className="text-left">{note}</p>}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 w-full">
            <div className="bg-[#ed1c24] rounded-xl size-6 flex items-center justify-center shrink-0">
              <img src={`${ICONS}/check-white.svg`} alt="" className="shrink-0" />
            </div>
            <p className="text-[#6b7280] text-[12px] font-medium flex-1 min-w-0 text-left">
              Please ensure you have read and understood all the above points before proceeding to payment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutMob;
